import { useEffect } from "react";
import { useFormik } from 'formik';
import * as yup from 'yup';
import { toast } from "sonner";
import { ActionIcon, Button, Modal, Text } from "rizzui";
import { IoClose } from "react-icons/io5";
import { WindTurbineService } from "../../../../../services/windTurbine/windTurbineService";
import useHandleErrors from "../../../../../hooks/useHandleErrors";
import { usePrivilege } from "../../../../../components/priviledge/PriviledgeProvider";
import RequiredInput from "../../../../../components/forms/InputHOC";
import CoordinatesInputs from "../../../../../components/commonForm/CoordinatesInputs";


interface ParkTurbinesModalProps {
    isOpen: boolean;
    onClose: () => void;
    refetch: () => void;
    windTurbine: any;
    parkId?: string;
}


const schema = yup.object({
    name: yup.string().required('Campo obligatorio'),
    alias: yup.string().required('Campo obligatorio'),
    shaft: yup.string().required('Campo obligatorio'),
    diameter: yup.string().required('Campo obligatorio'),
    capacity: yup.string().required('Campo obligatorio'),
    technology: yup.string().required('Campo obligatorio'),
    huso: yup.string().required('Campo requerido').test('spain_timezone', 'Debe de ser un huso horario de España (29, 30, 31)', (value) => {
        if (value === null || value === undefined || value === "") return true;
        const regex = /^(29|30|31)/;
        return regex.test(value);
    }),
    coordinatesX: yup.string().required('Campo requerido').test('is-decimal', 'El valor debe de estar entre 160000 y 870000', function (value) {
        if (Number(value) >= 160000 && Number(value) <= 870000) {
            return true;
        } 
        return false;
    }),
    coordinatesY: yup.string().required('Campo requerido').test('is-decimal', 'El valor debe de estar entre 3990000 y 4800000', function (value) {
        if (Number(value) >= 3990000 && Number(value) <= 4800000) {
            return true;
        } 
        return false;
    }),
});

const ParkTurbinesModal: React.FC<ParkTurbinesModalProps> = ({isOpen, onClose, refetch, windTurbine, parkId }) => {
    
    // STATES

    const service = new WindTurbineService();
    const isEdit = windTurbine !== null;
   
    
    // HOOKS

    const { handleErrors } = useHandleErrors();
    const { userCan } = usePrivilege();

    // FORMIK

    const formik = useFormik({
        initialValues: {
            name  : '',
            alias : '',
            shaft: '',
            diameter: '',
            capacity: '',
            technology: '',
            huso: '',
            coordinatesX: '',
            coordinatesY: '',
            parkId: parkId,
        },
        validationSchema: schema,
        validateOnBlur: false,
        onSubmit: values => { 
            if(!isEdit) {
                handleCreate(values)
            } else {
                handleEdit(values)
            }
        },
    });


    // FUNCTIONS

    //-----------------------------------------------------------------------------------------------------------------
    /**
     * @ES CREAR UN NUEVO AEROGENERADOR
     * @EN CREATE A NEW WIND TURBINE
     * 
     * @param values
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleCreate = async (values: any) => {
        try {
            const response = await (await service.createWindTurbine(values)).getResponseData();
            if (response.success) {
                refetch();
                onClose();
                formik.resetForm();
                toast.success('Aerogenerador creado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-----------------------------------------------------------------------------------------------------------------
    

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES EDITAR UN AEROGENERADOR
     * @EN EDIT A WIND TURBINE
     * 
     * @param values
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleEdit = async (values: any) => {
        try {
            values.id = windTurbine.id;
            const response = await (await service.editWindTurbine(values)).getResponseData();
            if (response.success) {
                refetch();
                onClose();
                formik.resetForm();
                toast.success('Aerogenerador editado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------


    // USE EFFECT

    useEffect(() => {
        if (isEdit) {
            formik.setFieldValue('name', windTurbine.name);
            formik.setFieldValue('alias', windTurbine.alias);
            formik.setFieldValue('shaft', windTurbine.shaft);
            formik.setFieldValue('diameter', windTurbine.diameter);
            formik.setFieldValue('capacity', windTurbine.capacity);
            formik.setFieldValue('technology', windTurbine.technology);            
            let coordinates = windTurbine?.coordinates?.split(',');
            if (coordinates) {
                formik.setFieldValue('huso',coordinates[0]);
                formik.setFieldValue('coordinatesX', coordinates[1]);
                formik.setFieldValue('coordinatesY', coordinates[2]);
            }
        }
        else{
            formik.resetForm();
        }
    }, [windTurbine]);

    // RENDER

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size='sm'
            overlayClassName='backdrop-blur'
            containerClassName='!max-w-xl !shadow-xl'
            className='z-[9999] [&_.pointer-events-none]:overflow-visible'
        >
            <div className='m-auto px-7 pt-6 pb-2'>
                <div className='mb-7 flex items-center justify-between'>
                    <Text className="text-xl">{isEdit ? 'Editar ' : 'Crear '} Aerogenerador</Text>
                    <ActionIcon size='sm' variant='text' onClick={onClose}>
                        <IoClose className='h-auto w-6' strokeWidth={1.8} />
                    </ActionIcon>
                </div>

                <RequiredInput
                    id='name'
                    label="Nombre"
                    formik={formik}
                    required
                    className="mb-4"
                />

                <RequiredInput
                    id='alias'
                    label="Alias"
                    formik={formik}
                    required
                    className="mb-2"
                />

                <RequiredInput
                    id='shaft'
                    label="Fuste"
                    formik={formik}
                    required
                    className="mb-2"
                    type={'number'}
                    suffix='metros'
                />

                <RequiredInput
                    id='diameter'
                    label="Diametro"
                    formik={formik}
                    required
                    className="mb-2"
                    type={'number'}
                    suffix='metros'
                />

                <RequiredInput
                    id='capacity'
                    type={'number'}
                    label="Capacidad"
                    formik={formik}
                    required
                    className="mb-2"
                    suffix='MW'
                />

                <RequiredInput
                    id='technology'
                    label="Tecnologia"
                    formik={formik}
                    required
                    className="mb-2"
                    placeholder="Enercon, Gamesa, etc"
                />

                <CoordinatesInputs formik={formik} />

            </div>

            <div className='flex justify-center p-7'>
                <Button
                    disabled={false}
                    onClick={() => formik.handleSubmit()}
                    color='primary'
                >
                    {isEdit ? 'Editar' : 'Crear'}
                </Button>
            </div>
        </Modal>
    )
}


export default ParkTurbinesModal;